import { useState } from "react";
import { Star, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { TID } from "@/constants/testIds";

const StarRow = ({ value, onChange, size = 18, interactive = false }) => (
  <div className="flex gap-1" data-testid={interactive ? TID.reviewRating : undefined}>
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onChange && onChange(i)}
        className={interactive ? "cursor-pointer" : "cursor-default"}
        data-testid={interactive ? `review-star-${i}` : undefined}
      >
        <Star
          size={size}
          strokeWidth={0}
          fill={i <= value ? "#D4AF37" : "#3a3a3a"}
        />
      </button>
    ))}
  </div>
);

export const Testimonials = ({ reviews, onSubmitted }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", body: "", service_type: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.body) {
      toast.error("Please fill in your name and review.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );
      await api.post("/reviews", payload);
      toast.success("Thanks! Your review is pending approval.");
      setForm({ name: "", email: "", rating: 5, title: "", body: "", service_type: "" });
      setShowForm(false);
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-24 lg:py-32 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline text-[#D4AF37] mb-4">Client Reviews</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
              The work <span className="gold-grad-text italic">speaks for itself.</span>
            </h2>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid={TID.reviewAddBtn}
            className="btn-ghost self-start"
          >
            {showForm ? <X size={16} /> : <Plus size={16} />}
            {showForm ? "Cancel" : "Leave a Review"}
          </button>
        </Reveal>

        {showForm && (
          <form
            onSubmit={submit}
            className="card-surface rounded-2xl p-7 lg:p-10 mb-12 fade-up"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="overline text-white/50 block mb-2">Your Name *</label>
                <input className="input-line" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid={TID.reviewName} required />
              </div>
              <div>
                <label className="overline text-white/50 block mb-2">Email (private)</label>
                <input type="email" className="input-line" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
              </div>
            </div>
            <div className="mt-6">
              <label className="overline text-white/50 block mb-2">Rating *</label>
              <StarRow value={form.rating} onChange={(v) => setForm({...form, rating: v})} interactive size={28} />
            </div>
            <div className="mt-6">
              <label className="overline text-white/50 block mb-2">Headline</label>
              <input className="input-line" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="e.g. Spotless move-out turnover" data-testid={TID.reviewTitle} />
            </div>
            <div className="mt-6">
              <label className="overline text-white/50 block mb-2">Your Review *</label>
              <textarea className="input-line" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} rows={4} data-testid={TID.reviewBody} required />
            </div>
            <button type="submit" disabled={submitting} className="btn-gold mt-8" data-testid={TID.reviewSubmit}>
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
            <p className="mt-4 text-xs text-white/40">Reviews are moderated before appearing publicly.</p>
          </form>
        )}

        {reviews.length === 0 ? (
          <div className="text-white/50 italic">No reviews yet — be the first to share your experience.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={TID.reviewsList}>
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 100}>
                <div className="card-surface rounded-2xl p-7 flex flex-col h-full">
                  <StarRow value={r.rating} />
                  {r.title && <h3 className="font-serif-display text-xl text-white mt-4">{r.title}</h3>}
                  <p className="mt-3 text-white/70 leading-relaxed flex-grow">"{r.body}"</p>
                  <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between">
                    <div className="text-sm text-white/80">{r.name}</div>
                    <div className="text-xs text-white/40">
                      {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
